import { useAxiosFormSubmitter } from 'shared/hooks/useBaseAxiosFormSubmitter';
import { dispatchRocEvent } from 'shared/hooks/useRocEventHandler';

/**
 * The values that are sent to the server when logging in.
 */
export interface LoginModel {
	/**
	 * The email address when logging in.
	 * This is utilized when `ServerData.USE_EMAIL_AS_USERNAME` is `true`.
	 */
	email: string;
	/**
	 * The username when logging in.
	 * This is utilized when `ServerData.USE_EMAIL_AS_USERNAME` is `false`.
	 */
	userName: string;
	/** The password when logging in. */
	password: string;
	/**
	 * The flag indicating whether the user logging in selected 'Remember Me' option.
	 */
	rememberMe: boolean;
}

/**
 * The data
 */
export interface LoginResponse {
	/**
	 * Whether or not the login attempt requires an additional two factor authentication step. This will only be flagged
	 * `true` when `success` is true - meaning that the password login succeeded but the user is required to present
	 * their second authentication factor.
	 */
	requiresTwoFactor: boolean;
	/**
	 * Whether the login was successful. Note that if a user account requires two factor authentication, this will
	 * still be `true`, check the `requiresTwoFactor` field to determine if the 2FA login step is required.
	 */
	success: boolean;
	/**
	 * The flag indicating whether the user logging in selected 'Remember Me' option.
	 */
	rememberMe: boolean;
}

/**
 * Custom hook that can be utilized when a login needs to be performed.
 */
export default function useLogin() {
	return useAxiosFormSubmitter<LoginModel, LoginResponse>(
		{
			url: '/ajax/user/login',
		},
		'Login',
		true,
		(response) => {
			if (response?.success) {
				dispatchRocEvent('user-logged-in', {});
			}
		},
	);
}
